import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import AutomationsAPI from '../../api/wgpt/automations';
import { throwErrorMessage } from '../utils/api';

export const state = {
  records: [],
  automationsList: [],
  clipboard: null,
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
    isCloning: false,
    isExporting: false,
    isImporting: false,
  },
};

export const getters = {
  getAutomations($state) {
    return $state.records;
  },
  getAutomationsList($state) {
    return $state.automationsList;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getAutomation: $state => automationId => {
    const automation = $state.records.find(
      record => record.id === Number(automationId)
    );
    return automation || {};
  },
  getActionClipboard($state) {
    return $state.clipboard;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isFetching: true });
    try {
      const response = await AutomationsAPI.get();
      commit(types.SET_WGPT_AUTOMATIONS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isFetching: false });
    }
  },
  getList: async ({ commit }) => {
    commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isFetching: true });
    try {
      const response = await AutomationsAPI.list();
      commit(types.SET_WGPT_AUTOMATIONS_LIST, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isFetching: false });
    }
  },
  create: async ({ commit }, automationObj) => {
    commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isCreating: true });
    try {
      const response = await AutomationsAPI.create(automationObj);
      commit(types.ADD_WGPT_AUTOMATION, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isCreating: false });
    }
    return null;
  },
  update: async ({ commit }, { id, ...automationObj }) => {
    commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isUpdating: true });
    try {
      const response = await AutomationsAPI.update(id, automationObj);
      commit(types.EDIT_WGPT_AUTOMATION, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isDeleting: true });
    try {
      await AutomationsAPI.delete(id);
      commit(types.DELETE_WGPT_AUTOMATION, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isDeleting: false });
    }
  },
  show: async ({ commit }, id) => {
    commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isFetchingItem: true });
    try {
      const { data } = await AutomationsAPI.show(id);
      commit(types.ADD_WGPT_AUTOMATION, data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isFetchingItem: false });
    }
  },
  clone: async ({ commit }, id) => {
    commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isCloning: true });
    try {
      await AutomationsAPI.clone(id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isCloning: false });
    }
  },
  addFile: async ({ commit }, { id: automationId, file }) => {
    commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isUpdating: true });
    try {
      const id = Number(automationId);
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await AutomationsAPI.addFile(id, formData);
      const { files } = data;
      commit(types.EDIT_WGPT_AUTOMATION, { id, files });
      return files[files.length - 1];
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  copyAction: ({ commit }, action) => {
    commit(types.SET_WGPT_AUTOMATION_ACTION_CLIPBOARD, {
      action,
      operation: 'copy',
    });
  },
  cutAction: ({ commit }, action) => {
    commit(types.SET_WGPT_AUTOMATION_ACTION_CLIPBOARD, {
      action,
      operation: 'cut',
    });
  },
  pasteAction: ({ commit, state: _state }) => {
    const { clipboard } = _state;
    if (!clipboard) return null;

    if (clipboard.operation === 'cut') {
      commit(types.SET_WGPT_AUTOMATION_ACTION_CLIPBOARD, null);
    }

    return clipboard;
  },
  export: async ({ commit }, id) => {
    commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isExporting: true });
    try {
      const { data } = await AutomationsAPI.export(id);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isExporting: false });
    }
    return null;
  },
  import: async ({ commit }, file) => {
    commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isImporting: true });
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await AutomationsAPI.import(formData);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_AUTOMATION_UI_FLAG, { isImporting: false });
    }
    return null;
  },
};

export const mutations = {
  [types.SET_WGPT_AUTOMATION_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.ADD_WGPT_AUTOMATION]: MutationHelpers.setSingleRecord,
  [types.SET_WGPT_AUTOMATIONS]: MutationHelpers.set,
  [types.SET_WGPT_AUTOMATIONS_LIST]($state, data) {
    $state.automationsList = data;
  },
  [types.EDIT_WGPT_AUTOMATION]: MutationHelpers.update,
  [types.DELETE_WGPT_AUTOMATION]: MutationHelpers.destroy,
  [types.SET_WGPT_AUTOMATION_ACTION_CLIPBOARD]($state, data) {
    $state.clipboard = data;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
